import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  TicketIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  AtSymbolIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/core/auth';
import { ticketingAPI } from './api/ticketing.api';
import { useTicketMentionCount } from '../../hooks';
import type {
  Ticket,
  TicketCategory,
  TicketStats,
  TicketFilters,
  TicketStatus,
  TicketPriority,
  TicketSource,
  ClaudeStatus,
} from './types/ticketing.types';
import { TicketModal } from './components/TicketModal';
import { TicketDetailModal } from './components/TicketDetailModal';
import './TicketingTab.css';

const STATUS_CONFIG: Record<TicketStatus, { label: string; color: string }> = {
  open: { label: 'Ouvert', color: '#3b82f6' },
  in_progress: { label: 'En cours', color: '#f59e0b' },
  pending: { label: 'En attente', color: '#8b5cf6' },
  to_be_tested: { label: 'A tester', color: '#06b6d4' },
  ready_for_dev: { label: 'Pret pour dev', color: '#22c55e' },
  resolved: { label: 'Resolu', color: '#10b981' },
  closed: { label: 'Ferme', color: '#6b7280' },
  cancelled: { label: 'Annule', color: '#ef4444' },
};

const DEFAULT_STATUS = { label: 'Inconnu', color: '#9ca3af' };
const getStatusConfig = (status: string) => STATUS_CONFIG[status as TicketStatus] || DEFAULT_STATUS;

const PRIORITY_CONFIG: Record<TicketPriority, { label: string; color: string; icon: React.ReactNode }> = {
  low: { label: 'Basse', color: '#6b7280', icon: null },
  medium: { label: 'Moyenne', color: '#3b82f6', icon: null },
  high: { label: 'Haute', color: '#f59e0b', icon: <ExclamationTriangleIcon className="w-4 h-4" /> },
  urgent: { label: 'Urgente', color: '#ef4444', icon: <ExclamationTriangleIcon className="w-4 h-4" /> },
};

const DEFAULT_PRIORITY = { label: 'Inconnu', color: '#9ca3af', icon: null };
const getPriorityConfig = (priority: string) => PRIORITY_CONFIG[priority as TicketPriority] || DEFAULT_PRIORITY;

const SOURCE_CONFIG: Record<TicketSource, { label: string; color: string }> = {
  webapp: { label: 'WebApp', color: '#3b82f6' },
  pickit: { label: 'PickIt', color: '#10b981' },
  revelo: { label: 'Revelo', color: '#f97316' },
};

const DEFAULT_SOURCE = { label: 'Inconnu', color: '#9ca3af' };
const getSourceConfig = (source: string) => SOURCE_CONFIG[source as TicketSource] || DEFAULT_SOURCE;

// Claude processing status configuration
const CLAUDE_STATUS_CONFIG: Record<NonNullable<ClaudeStatus>, { label: string; color: string; shortLabel: string }> = {
  pending: { label: 'En attente Claude', color: '#8b5cf6', shortLabel: 'Attente' },
  analyzing: { label: 'Claude analyse', color: '#f59e0b', shortLabel: 'Analyse' },
  needs_clarification: { label: 'Question en attente', color: '#ef4444', shortLabel: 'Question' },
  awaiting_response: { label: 'Reponse attendue', color: '#f97316', shortLabel: 'Reponse' },
  prd_created: { label: 'PRD cree', color: '#22c55e', shortLabel: 'PRD' },
  implementation_done: { label: 'Implemente', color: '#10b981', shortLabel: 'Fait' },
  testing: { label: 'A tester', color: '#06b6d4', shortLabel: 'Test' },
  completed: { label: 'Termine', color: '#6b7280', shortLabel: 'OK' },
  failed: { label: 'Erreur Claude', color: '#dc2626', shortLabel: 'Erreur' },
};

const getClaudeStatusConfig = (status: ClaudeStatus) => {
  if (!status) return null;
  return CLAUDE_STATUS_CONFIG[status] || null;
};

export const TicketingTab: React.FC = () => {
  const { user } = useAuth();
  const { refetch: refetchMentionCount } = useTicketMentionCount();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, totalPages: 0 });

  const [filters, setFilters] = useState<TicketFilters>({
    view: 'all',
    sort_by: 'created_at',
    sort_order: 'DESC',
    page: 1,
    limit: 25,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch initial data
  useEffect(() => {
    fetchCategories();
    fetchStats();
  }, []);

  // Fetch tickets when filters change or refresh is triggered
  useEffect(() => {
    const loadTickets = async () => {
      try {
        setLoading(true);
        const response = await ticketingAPI.getTickets({
          ...filters,
          search: searchTerm || undefined,
        });
        if (response.success) {
          setTickets(response.data);
          setPagination(response.pagination);
        }
      } catch (error) {
        console.error('Error fetching tickets:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTickets();
  }, [filters, refreshKey, searchTerm]);

  const fetchCategories = async () => {
    try {
      const response = await ticketingAPI.getCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await ticketingAPI.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
    setRefreshKey(prev => prev + 1);
  };

  const handleStatusFilter = (status?: TicketStatus) => {
    setFilters(prev => ({ ...prev, status, page: 1 }));
  };

  const handleSort = (column: TicketFilters['sort_by']) => {
    setFilters(prev => {
      const newOrder = prev.sort_by === column && prev.sort_order === 'DESC' ? 'ASC' : 'DESC';
      return { ...prev, sort_by: column, sort_order: newOrder, page: 1 };
    });
  };

  const renderSortIcon = (column: TicketFilters['sort_by']) => {
    if (filters.sort_by !== column) {
      return <ChevronUpDownIcon className="w-4 h-4 sort-icon inactive" />;
    }
    return filters.sort_order === 'DESC'
      ? <ChevronDownIcon className="w-4 h-4 sort-icon active" />
      : <ChevronUpIcon className="w-4 h-4 sort-icon active" />;
  };

  const handleTicketCreated = () => {
    setIsCreateModalOpen(false);
    setRefreshKey(prev => prev + 1);
    fetchStats();
  };

  const handleTicketUpdated = () => {
    setRefreshKey(prev => prev + 1);
    fetchStats();
    if (selectedTicket) {
      // Refresh the selected ticket
      ticketingAPI.getTicketById(selectedTicket.id).then(res => {
        if (res.success) {
          setSelectedTicket(res.data);
        }
      });
    }
  };

  const handleMentionRead = useCallback(() => {
    refetchMentionCount();
    setRefreshKey(prev => prev + 1);
  }, [refetchMentionCount]);

  const handleOpenTicket = async (ticket: Ticket) => {
    // Fetch full ticket with attachments first (includes metadata.attachments)
    try {
      const res = await ticketingAPI.getTicketById(ticket.id);
      setSelectedTicket(res.success ? res.data : ticket);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      setSelectedTicket(ticket);
    }
    setIsDetailModalOpen(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      return `il y a ${diffMins} min`;
    } else if (diffHours < 24) {
      return `il y a ${diffHours}h`;
    } else if (diffDays < 7) {
      return `il y a ${diffDays}j`;
    } else {
      return date.toLocaleDateString('fr-BE', { day: '2-digit', month: 'short' });
    }
  };

  return (
    <div className="ticketing-tab">
      {/* Stats Cards */}
      {stats && (
        <div className="ticketing-stats">
          <div
            className={`ticketing-stat-card clickable ${!filters.status ? 'active' : ''}`}
            onClick={() => handleStatusFilter(undefined)}
          >
            <div className="ticketing-stat-icon all">
              <CalendarDaysIcon className="w-5 h-5" />
            </div>
            <div className="ticketing-stat-info">
              <span className="ticketing-stat-value">{Number(stats.open_count) + Number(stats.in_progress_count) + Number(stats.pending_count)}</span>
              <span className="ticketing-stat-label">Total actifs</span>
            </div>
          </div>

          <div
            className={`ticketing-stat-card clickable ${filters.status === 'open' ? 'active' : ''}`}
            onClick={() => handleStatusFilter(filters.status === 'open' ? undefined : 'open')}
          >
            <div className="ticketing-stat-icon open">
              <TicketIcon className="w-5 h-5" />
            </div>
            <div className="ticketing-stat-info">
              <span className="ticketing-stat-value">{stats.open_count}</span>
              <span className="ticketing-stat-label">Ouverts</span>
            </div>
          </div>

          <div
            className={`ticketing-stat-card clickable ${filters.status === 'in_progress' ? 'active' : ''}`}
            onClick={() => handleStatusFilter(filters.status === 'in_progress' ? undefined : 'in_progress')}
          >
            <div className="ticketing-stat-icon in-progress">
              <ClockIcon className="w-5 h-5" />
            </div>
            <div className="ticketing-stat-info">
              <span className="ticketing-stat-value">{stats.in_progress_count}</span>
              <span className="ticketing-stat-label">En cours</span>
            </div>
          </div>

          <div
            className={`ticketing-stat-card clickable ${filters.status === 'resolved' ? 'active' : ''}`}
            onClick={() => handleStatusFilter(filters.status === 'resolved' ? undefined : 'resolved')}
          >
            <div className="ticketing-stat-icon resolved">
              <CheckCircleIcon className="w-5 h-5" />
            </div>
            <div className="ticketing-stat-info">
              <span className="ticketing-stat-value">{stats.resolved_count || 0}</span>
              <span className="ticketing-stat-label">Résolus</span>
            </div>
          </div>

        </div>
      )}

      {/* Controls */}
      <div className="ticketing-controls">
        <div className="ticketing-controls-left">
          {/* Search */}
          <div className="ticketing-search">
            <MagnifyingGlassIcon className="ticketing-search-icon" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(e);
                }
              }}
              className="ticketing-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                className="ticketing-search-clear"
                onClick={() => {
                  setSearchTerm('');
                  setFilters(prev => ({ ...prev, page: 1 }));
                }}
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="ticketing-controls-right">
          {/* Status Filter Pills */}
          <div className="ticketing-status-filters">
            <button
              className={`ticketing-status-btn ${!filters.status ? 'active' : ''}`}
              onClick={() => handleStatusFilter(undefined)}
            >
              Tous
            </button>
            <button
              className={`ticketing-status-btn ${filters.status === 'open' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('open')}
            >
              Ouverts
            </button>
            <button
              className={`ticketing-status-btn ${filters.status === 'in_progress' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('in_progress')}
            >
              En cours
            </button>
            <button
              className={`ticketing-status-btn ${filters.status === 'resolved' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('resolved')}
            >
              Résolus
            </button>
            <button
              className={`ticketing-status-btn ${filters.status === 'closed' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('closed')}
            >
              Fermés
            </button>
          </div>

          {/* New Ticket Button */}
          <button
            className="ticketing-btn ticketing-btn-primary"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <PlusIcon className="w-5 h-5" />
            <span>Nouveau ticket</span>
          </button>
        </div>
      </div>

      {/* Ticket List */}
      <div className="ticketing-list">
        {loading ? (
          <div className="ticketing-loading">
            <div className="ticketing-spinner" />
            <span>Chargement...</span>
          </div>
        ) : tickets.length === 0 ? (
          <div className="ticketing-empty">
            <ChatBubbleLeftRightIcon className="w-12 h-12" />
            <h3>Aucun ticket</h3>
            <p>Il n'y a pas de tickets correspondant a vos criteres.</p>
            <button
              className="ticketing-btn ticketing-btn-primary"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <PlusIcon className="w-5 h-5" />
              <span>Creer un ticket</span>
            </button>
          </div>
        ) : (
          <>
            <div className="ticketing-table">
              <div className="ticketing-table-header">
                <div
                  className="ticketing-col-number sortable"
                  onClick={() => handleSort('ticket_number')}
                >
                  # {renderSortIcon('ticket_number')}
                </div>
                <div
                  className="ticketing-col-title sortable"
                  onClick={() => handleSort('title')}
                >
                  Titre {renderSortIcon('title')}
                </div>
                <div
                  className="ticketing-col-category sortable"
                  onClick={() => handleSort('category_name')}
                >
                  Catégorie {renderSortIcon('category_name')}
                </div>
                <div
                  className="ticketing-col-author sortable"
                  onClick={() => handleSort('created_by_name')}
                >
                  Auteur {renderSortIcon('created_by_name')}
                </div>
                <div
                  className="ticketing-col-priority sortable"
                  onClick={() => handleSort('priority')}
                >
                  Priorité {renderSortIcon('priority')}
                </div>
                <div
                  className="ticketing-col-status sortable"
                  onClick={() => handleSort('status')}
                >
                  Statut {renderSortIcon('status')}
                </div>
                <div
                  className="ticketing-col-claude sortable"
                  onClick={() => handleSort('claude_status')}
                >
                  Claude {renderSortIcon('claude_status')}
                </div>
                <div
                  className="ticketing-col-date sortable"
                  onClick={() => handleSort('created_at')}
                >
                  Date {renderSortIcon('created_at')}
                </div>
                <div
                  className="ticketing-col-activity sortable"
                  onClick={() => handleSort('last_activity')}
                >
                  Activité {renderSortIcon('last_activity')}
                </div>
              </div>

              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`ticketing-table-row ${ticket.has_unread_mentions ? 'mentioned' : ticket.is_unread ? 'unread' : ''}`}
                  onClick={() => handleOpenTicket(ticket)}
                >
                  <div className="ticketing-col-number">
                    <span className="ticket-number">#{ticket.ticket_number}</span>
                    {ticket.source && (
                      <span
                        className="ticket-source-badge"
                        style={{
                          backgroundColor: `${getSourceConfig(ticket.source || '').color}15`,
                          color: getSourceConfig(ticket.source || '').color,
                        }}
                      >
                        {getSourceConfig(ticket.source || '').label}
                      </span>
                    )}
                  </div>
                  <div className="ticketing-col-title">
                    <span className="ticket-title">{ticket.title}</span>
                    {ticket.has_unread_mentions && (
                      <span className="ticket-mention-indicator" title="Vous etes mentionne">
                        <AtSymbolIcon className="w-4 h-4" />
                      </span>
                    )}
                    {ticket.comment_count && ticket.comment_count > 0 && (
                      <span className="ticket-comments">
                        <ChatBubbleLeftRightIcon className="w-4 h-4" />
                        {ticket.comment_count}
                      </span>
                    )}
                  </div>
                  <div className="ticketing-col-category">
                    {ticket.category_name && (
                      <span
                        className="ticket-category"
                        style={{ backgroundColor: `${ticket.category_color}20`, color: ticket.category_color }}
                      >
                        {ticket.category_name}
                      </span>
                    )}
                  </div>
                  <div className="ticketing-col-author">
                    <span className="ticket-author">{ticket.created_by_name}</span>
                  </div>
                  <div className="ticketing-col-priority">
                    <span
                      className="ticket-priority"
                      style={{ color: getPriorityConfig(ticket.priority).color }}
                    >
                      {getPriorityConfig(ticket.priority).icon}
                      {getPriorityConfig(ticket.priority).label}
                    </span>
                  </div>
                  <div className="ticketing-col-status">
                    <span
                      className="ticket-status"
                      style={{
                        backgroundColor: `${getStatusConfig(ticket.status).color}20`,
                        color: getStatusConfig(ticket.status).color,
                      }}
                    >
                      {getStatusConfig(ticket.status).label}
                    </span>
                  </div>
                  <div className="ticketing-col-claude">
                    {ticket.claude_status &&
                     !['resolved', 'closed'].includes(ticket.status) &&
                     getClaudeStatusConfig(ticket.claude_status) && (
                      <span
                        className="ticket-claude-status"
                        style={{
                          backgroundColor: `${getClaudeStatusConfig(ticket.claude_status)!.color}15`,
                          color: getClaudeStatusConfig(ticket.claude_status)!.color,
                          borderColor: `${getClaudeStatusConfig(ticket.claude_status)!.color}40`,
                        }}
                        title={getClaudeStatusConfig(ticket.claude_status)!.label}
                      >
                        {getClaudeStatusConfig(ticket.claude_status)!.shortLabel}
                      </span>
                    )}
                  </div>
                  <div className="ticketing-col-date">
                    <ClockIcon className="w-4 h-4" />
                    {formatDate(ticket.created_at)}
                  </div>
                  <div className="ticketing-col-activity">
                    <span className="ticket-activity-time">
                      {formatDate(ticket.last_comment_at || ticket.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="ticketing-pagination">
                <button
                  className="ticketing-page-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page! - 1 }))}
                >
                  Precedent
                </button>
                <span className="ticketing-page-info">
                  Page {pagination.page} sur {pagination.totalPages}
                </span>
                <button
                  className="ticketing-page-btn"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page! + 1 }))}
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <TicketModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleTicketCreated}
          categories={categories}
        />
      )}

      {/* Detail Modal */}
      {isDetailModalOpen && selectedTicket && (
        <TicketDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedTicket(null);
          }}
          ticket={selectedTicket}
          onUpdate={handleTicketUpdated}
          onMentionRead={handleMentionRead}
          categories={categories}
          userRole={user?.role}
        />
      )}
    </div>
  );
};

export default TicketingTab;
